import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import useParam from '../../../../context/ParamContext';
import { useLoader } from '../../../../provider/LoaderProvider';
import simpletaskService from '../../../../service/simpletask.service';
import { ColorPicker } from '../../../../config/ColorPicker';
import EventChip from './EventChip';
import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';
import SimpleTaskDialog from './SimpleTaskDialog';
import useUsers from '../../../../context/UsersContext';
import useMenus from '../../../../context/MenuContext';
import SearchDialog from './SearchDialog';
import projectTaskResourcePlannerService from '../../../../service/client/caravan/projectTaskResourcePlanner.service';
import GanttFilter from './GanttFilter';
import masterDataService from '../../../../service/masterData.service';
import { generateAutocompleteItems, generateDefaultTime } from '../../../../utils/helper';
import useView from '../../../../hooks/useView';
import colorFinder from '../../../../utils/colorFinder';
import { Box, Tooltip, Typography } from '@mui/material';
import useConfig from '../../../../context/ConfigContext';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import PlanTaskDialog from './PlanTaskDialog';
import planTaskService from '../../../../service/planTask.service';
import PlanSearchDialog from './PlanSearchDialog';
import 'dayjs/locale/hu';
import EventChipMenu from './EventChipMenu';
import { ConfirmDialogUI } from '../../../Interface';

dayjs.extend(isSameOrBefore);
dayjs.locale('hu');

const ProjectTaskResourcePlanner = () => {
  const { config, getConfig } = useConfig();
  const selectedResourceRef = useRef(null);
  const checkboxFiltersRef = useRef(null);
  const calendarRef = useRef(null);
  const isDraggingRef = useRef(false);
  const { getUser, user } = useUsers();
  const { getParam, params } = useParam();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit, getSubMenuNamesByUrls } = useMenus();
  const { showLoader, hideLoader } = useLoader();
  const savedFilters = useView('projectTaskFilter', user);
  const [tasks, setTasks] = useState({ resources: [], taskEvents: [], backgroundEvents: [] });
  const [dialogOpen, setDialogOpen] = useState({ open: false, editing: false, type: null });
  const [task, setTask] = useState({});
  const [dateRange, setDateRange] = useState({});
  const [taskChanged, setTaskChanged] = useState(false);
  const [timeInterval, setTimeInterval] = useState({});
  const [intervalButtons, setIntervalButtons] = useState({});
  const [calendarViews, setCalendarViews] = useState({});
  const [selectedView, setSelectedView] = useState('');
  const [canEdit, setCanEdit] = useState(null);
  const [dialogData, setDialogData] = useState({});
  // add: long-press state
  const [longPressActive, setLongPressActive] = useState(false);
  // add: search state
  const [searchQuery, setSearchQuery] = useState('');
  // add: search dialog open state
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [copyId, setCopyId] = useState(null);
  // add: header offset for fixed desktop header
  const [headerOffset, setHeaderOffset] = useState(0);
  const [initialState, setInitialState] = useState({
    filter: {
      filterModel: {
        items: [],
        logicOperator: 'and',
      },
    },
  });
  const [options, setOptions] = useState({
    types: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRowData, setSelectedRowData] = useState({});
  const [hoverDate, setHoverDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const defChipMenu = {
    data: {},
    position: null,
  };
  const [chipMenu, setChipMenu] = useState(defChipMenu);
  const defConfirmDialogData = {
    open: false,
    message: '',
    handleChangeElement: () => {},
    onHide: () => {},
  };
  const [confirmDialogData, setConfirmDialogData] = useState(defConfirmDialogData);

  const slotIntervals = {
    halfHour: { hours: 0.5 },
    hour: { hours: 1 },
    day: { days: 1 },
    week: { weeks: 1 },
    month: { months: 1 },
  };

  const calendarHourViews = {
    resourceTimelineDay: {
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [
        { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' },
        { hour: 'numeric', minute: '2-digit' },
      ],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [
        { day: 'numeric', weekday: 'long' },
        { hour: 'numeric', minute: '2-digit' },
      ],
    },
  };

  const calendarDayViews = {
    resourceTimelineDay: {
      slotLabelFormat: [{ month: 'short', day: 'numeric' }, { weekday: 'narrow' }],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [{ day: 'numeric', weekday: 'long' }],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [{ day: 'numeric', weekday: 'short' }],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ month: 'short' }, { day: 'numeric' }, { weekday: 'narrow' }],
    },
  };

  const calendarWeekViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
  };

  const calendarMonthViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ month: 'long' }],
    },
  };

  const refreshCalendar = () => {
    const calendarApi = calendarRef.current.getApi();
    setTimeout(() => {
      calendarApi.render();
    }, 100);
  };

  useEffect(() => {
    refreshCalendar();
  }, [tasks.taskEvents]);

  useEffect(() => {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    setTimeInterval(slotIntervals.day);
    setCalendarViews(calendarDayViews);

    // @TODO promiseArray
    getParam();
    getMenus();
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();

    const toolbarElement = document.querySelector('.fc-toolbar-chunk');
    if (toolbarElement && checkboxFiltersRef.current) {
      toolbarElement.appendChild(checkboxFiltersRef.current);
    }
    initDefaultValues();
  }, []);

  useEffect(() => {
    if (Object.keys(user).length > 0 && !isLoading) getProjects();
  }, [initialState, user, isLoading]);

  useEffect(() => {
    const indicator = document.querySelector('.fc-now-indicator');
    if (!indicator) return;

    const getScrollableParent = (el) => {
      let p = el?.parentElement;
      while (p) {
        const cs = getComputedStyle(p);
        const canScrollX = /(auto|scroll)/.test(cs.overflowX);
        if (canScrollX && p.scrollWidth > p.clientWidth) return p;
        p = p.parentElement;
      }
      return null;
    };

    const container = getScrollableParent(indicator);

    if (!container) {
      // Fallback: try built-in centering
      indicator.scrollIntoView({ block: 'nearest', inline: 'center' });
      return;
    }

    requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect();
      const indicatorRect = indicator.getBoundingClientRect();

      // indicator center position relative to container scroll origin
      const indicatorCenterX = indicatorRect.left - containerRect.left + container.scrollLeft + indicatorRect.width / 2;

      const desiredLeft = Math.max(0, indicatorCenterX - container.clientWidth / 2);
      const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);

      // If centering would pass the right edge, go fully right
      container.scrollLeft = desiredLeft >= maxScrollLeft - 1 ? maxScrollLeft : desiredLeft;
    });
  }, [timeInterval, calendarViews, dateRange.startDate, dateRange.endDate]);

  // add: update header offset based on breakpoint (lg: 1024px)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setHeaderOffset(mq.matches ? 90 : 0);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    } else {
      mq.addListener(update);
      return () => mq.removeListener(update);
    }
  }, []);

  useEffect(() => {
    const subMenuId = getMenuSubMenuId('caravan/projectTaskResourcePlanner');
    setCanEdit(getCanEdit(subMenuId));
  }, [menus]);

  useEffect(() => {
    setIntervalButtons(generateButtons(selectedView));
  }, [selectedView]);

  useEffect(() => {
    const calendarEl = calendarRef.current?.getApi()?.el;
    if (!calendarEl) return;

    const handleMouseMove = (e) => {
      if (isDraggingRef.current) return;

      const highlight = e.target.closest('.fc-highlight');

      if (highlight && selectedRowData?.startDate && selectedRowData?.endDate) {
        const start = dayjs(selectedRowData.startDate);
        const end = dayjs(selectedRowData.endDate);

        const label = start.isSame(end, 'day')
          ? start.format('MMM-DD.')
          : `${start.format('MMM.DD.')} - ${end.format('MMM.DD.')}`;

        setHoverDate(label);
        return;
      }

      const cell = e.target.closest('.fc-timeline-slot-lane');
      if (cell) {
        const dateStr = cell.getAttribute('data-date');
        if (dateStr) {
          setHoverDate(`${dayjs(dateStr).format('MMM.DD.')}`);
        }
        return;
      }

      setHoverDate(null);
    };

    const handleMouseLeave = () => setHoverDate(null);

    calendarEl.addEventListener('mousemove', handleMouseMove);
    calendarEl.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      calendarEl.removeEventListener('mousemove', handleMouseMove);
      calendarEl.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [selectedRowData]);

  useEffect(() => {
    const header = document.querySelector('.fc-timeline-header');
    if (!header) return;

    const handler = (e) => {
      const cell = e.target.closest('[data-date]');
      if (!cell) return;

      const colspan = cell.getAttribute('colspan');
      if (colspan && Number(colspan) > 1) return;

      setSelectedDate((prev) => (prev === cell.dataset.date ? null : cell.dataset.date));
    };

    header.addEventListener('click', handler);
    return () => header.removeEventListener('click', handler);
  }, []);

  useEffect(() => {
    const calendarEl = calendarRef.current?.getApi()?.el;
    if (!calendarEl) return;

    if (!selectedDate) {
      calendarEl
        .querySelectorAll('.fc-timeline-slot.selected-column')
        .forEach((el) => el.classList.remove('selected-column'));
      return;
    }

    calendarEl
      .querySelectorAll('.fc-timeline-slot.selected-column')
      .forEach((el) => el.classList.remove('selected-column'));

    calendarEl
      .querySelectorAll(`.fc-timeline-slot[data-date="${selectedDate}"]`)
      .forEach((el) => el.classList.add('selected-column'));
  }, [selectedDate]);

  useEffect(() => {
    if (taskChanged) {
      getTasks();
      setTaskChanged(false);
    }
  }, [taskChanged]);

  useEffect(() => {
    if (canEdit !== null && Object.keys(user).length > 0 && dateRange.startDate && dateRange.endDate) getTasks();
  }, [dateRange.startDate, dateRange.endDate, user, canEdit]);

  const getProjects = () => {
    showLoader();
    projectTaskResourcePlannerService
      .getProjects(initialState)
      .then((data) => {
        setTasks((prevState) => ({ ...prevState, resources: data }));
        napokTomb(data);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const applySavedProjectTypeFilter = useCallback(() => {
    const ids = Array.isArray(savedFilters?.projectTypeId) ? savedFilters.projectTypeId : [];

    setInitialState((prev) => {
      const items = prev?.filter?.filterModel?.items ?? [];
      const others = items.filter((i) => i.field !== 'projectTypeId');

      const nextItems = ids.length
        ? [...others, { id: 'projectType', field: 'projectTypeId', operator: 'isAnyOf', value: ids }]
        : others;

      return {
        ...prev,
        filter: {
          filterModel: {
            items: nextItems,
            logicOperator: prev?.filter?.filterModel?.logicOperator ?? 'and',
          },
        },
      };
    });
  }, [savedFilters?.projectTypeId]);

  useEffect(() => {
    applySavedProjectTypeFilter();
  }, [applySavedProjectTypeFilter]);

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['simpleTask', 'planTask']);
  }, [menus]);

  const colorParam = params.PROJECTTASKPLANNER_CHIP_COLOR_THEME;
  const parsedParam = colorParam ? JSON.parse(colorParam) : [];

  const typeParam = params.RESOURCE_PLANNER_SHOW_TASKS_BY_URL;
  const type = typeParam || 'planTask';

  // add: open/close dialog based on query
  useEffect(() => {
    if (searchQuery && searchQuery.trim().length > 0) {
      setSearchDialogOpen(true);
    } else {
      setSearchDialogOpen(false);
    }
  }, [searchQuery]);

  const getTasks = () => {
    const body = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    //showLoader();
    projectTaskResourcePlannerService
      .getTask(body)
      .then((data) => {
        const taskEvents = data.map((task) => {
          const colorStyles = getColorStyles(task?.eventData, parsedParam[0], 'TASK');
          return {
            ...task,
            ...colorStyles,
          };
        });
        setTasks((prevState) => ({ ...prevState, taskEvents }));
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getTaskById = (simpleTaskId, editing = false) => {
    showLoader();
    simpletaskService
      .getTaskById(simpleTaskId)
      .then((data) => {
        setTask(data);
        setDialogOpen({ open: true, editing });
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getPlanTaskById = (id, editing = false) => {
    showLoader();
    planTaskService
      .getTaskById(id)
      .then((data) => {
        setTask(data);
        setDialogOpen({ open: true, editing });
      })
      .finally(() => {
        hideLoader();
      });
  };

  const initDefaultValues = () => {
    setIsLoading(true);
    masterDataService
      .projectTypeToForm()
      .then((data) => {
        const aData = {
          types: data,
        };

        const acOptions = {
          types: generateAutocompleteItems(aData.types, 'name', 'id'),
        };
        setOptions(acOptions);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const defaultStyles = { backgroundColor: '#fff', textColor: '#000', borderColor: '#2d3032' };
  const getColorStyles = (event, colorProperties, eventType) => {
    const colorProperty = colorProperties?.[eventType];
    if (!colorProperty) return defaultStyles;

    const colorName = colorProperty.split('.').reduce((obj, key) => obj?.[key], event);
    if (!colorName) return defaultStyles;

    const colorObj = ColorPicker.find((color) => color.colorName === colorName);
    return {
      ...defaultStyles,
      backgroundColor: colorObj?.colorBgCode || defaultStyles.backgroundColor,
      textColor: colorObj?.textColor || defaultStyles.textColor,
    };
  };

  const handleDatesSet = (dateInfo) => {
    const startDate = dayjs(dateInfo.start).startOf('day');
    const endDate = dayjs(dateInfo.end).endOf('day');

    if (
      !dateRange.startDate ||
      !dateRange.endDate ||
      !startDate.isSame(dateRange.startDate) ||
      !endDate.isSame(dateRange.endDate)
    ) {
      setDateRange({ startDate, endDate });
    }

    if (selectedView !== dateInfo.view.type) setSelectedView(dateInfo.view.type);
  };

  const dropParam = JSON.parse(params.RESOURCE_PLANNER_ON_DROP_OPTION || '{}')['caravan/projectTaskResourcePlanner'];

  const updateEvent = (resizeInfo) => {
    const { event, revert, oldEvent } = resizeInfo;
    const resource = event.getResources()[0].id;
    const oldResource = oldEvent.getResources()[0].id;

    const startDate = dayjs(event.start);
    const oldStart = dayjs(oldEvent.start);

    const isProject = resource.startsWith('P');

    const fail = (msg) => {
      addNotification({
        content: msg,
        type: NotificationType.WARNING,
      });
      revert();
    };

    if (!isProject) return fail('A foglalás nem helyezhető típusra!');

    const projectId = parseInt(resource.substring(1));
    const oldProjectId = parseInt(oldResource.substring(1));

    if (dropParam) {
      if (dropParam?.dropHorizontal !== 'Y' && dropParam?.dropVertical !== 'Y' && dropParam?.resize !== 'Y')
        return fail('A foglalás nem módosítható és nem helyezhető át!');
      if ('startDelta' in resizeInfo && dropParam?.resize !== 'Y')
        return fail('A foglalás időtartama nem módosítható!');
      if (projectId !== oldProjectId && dropParam?.dropVertical !== 'Y')
        return fail('A foglalás nem helyezhető át függőlegesen!');
      if (!startDate.isSame(oldStart, 'minute') && dropParam?.dropHorizontal !== 'Y' && !('startDelta' in resizeInfo))
        return fail('A foglalás nem helyezhető át vízszintesen!');
    }

    const durationInMinutes = event.end ? dayjs(event.end).diff(dayjs(event.start), 'minute') : null;
    const duration = durationInMinutes / 60;
    const endDate = event.end
      ? dayjs(event.end).toISOString()
      : dayjs(event.start).add(durationInMinutes, 'minute').toISOString();

    const updatedEventData = {
      projectId,
      startDate: startDate.toISOString(),
      endDate,
    };

    //showLoader();

    updatedEventData.duration = duration;
    projectTaskResourcePlannerService
      .updateTask(updatedEventData, event.id)
      .then((data) => {
        addNotification({
          content: 'Sikeres módosítás!',
          type: NotificationType.SUCCESS,
        });
      })
      .catch(() => {
        revert();
      });
  };

  const timeIntervalButtons = {
    halfHour: {
      text: 'Fél óra',
      click: () => {
        setTimeInterval(slotIntervals.halfHour);
        setCalendarViews(calendarHourViews);
      },
    },
    hour: {
      text: 'Óra',
      click: () => {
        setTimeInterval(slotIntervals.hour);
        setCalendarViews(calendarHourViews);
      },
    },
    day: {
      text: 'Nap',
      click: () => {
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);
      },
    },
    week: {
      text: 'Hét',
      click: () => {
        setTimeInterval(slotIntervals.week);
        setCalendarViews(calendarWeekViews);
      },
    },
    month: {
      text: 'Hónap',
      click: () => {
        setTimeInterval(slotIntervals.month);
        setCalendarViews(calendarMonthViews);
      },
    },
  };

  const generateButtons = (viewName) => {
    let retVal = {};
    switch (viewName) {
      case 'resourceTimelineDay':
        retVal = 'halfHour,hour,day';
        setTimeInterval(slotIntervals.halfHour);
        setCalendarViews(calendarHourViews);

        break;
      case 'resourceTimelineWeek':
        retVal = 'halfHour,hour,day,week';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
      case 'resourceTimelineMonth':
        retVal = 'hour,day,week,month';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
      case 'resourceTimelineYear':
        retVal = 'day,week,month';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
    }
    return retVal;
  };

  const handleDateClick = async (event) => {
    const { resource, date } = event;

    const effectiveSelectedRowData = copyId ? null : selectedRowData;

    let task = {};
    if (copyId) {
      switch (type) {
        case 'simpleTask':
          task = await simpletaskService.getTaskById(copyId);
          const { simpleTaskId, ...taskWithoutSimpleTaskId } = task;
          task = taskWithoutSimpleTaskId;
          break;
        case 'planTask':
          task = await planTaskService.getTaskById(copyId);
          const { id: planTaskId, ...taskWithoutId } = task;
          task = taskWithoutId;
          break;
        default:
          task = {};
      }
      setCopyId(null);
    }

    const startDateRaw = effectiveSelectedRowData?.startDate ? effectiveSelectedRowData.startDate : date;
    const startDate = generateDefaultTime(startDateRaw, config.defTaskFromTime);

    const endDateRaw = effectiveSelectedRowData?.endDate ? effectiveSelectedRowData.endDate : date;
    const endDate = generateDefaultTime(endDateRaw, config.defTaskToTime);

    const durationInMinutes = endDate.diff(startDate, 'minute');
    const duration = durationInMinutes / 60;
    const projectId = parseInt(resource.id.substring(1));

    let dialogData = {
      ...task,
      startDate,
      endDate,
      duration,
      projectId,
    };

    setDialogData(dialogData);
    setDialogOpen({ open: true });
  };

  const handleSelectPreview = (selectInfo) => {
    const { start, end } = selectInfo;
    const endDate = dayjs(end).startOf('day');
    const daysCount = endDate.diff(start, 'day');

    setSelectedRowData({
      startDate: dayjs(start).startOf('day').add(12, 'hour'),
      endDate: endDate.subtract(1, 'day').add(12, 'hour'),
      daysCount,
    });

    return true;
  };

  const napokTomb = (resources) => {
    const res = [];
    let d = dayjs(dateRange.startDate);
    const e = dayjs(dateRange.endDate);

    const resourceIds = resources.flatMap((group) => group.children?.map((child) => child.id) ?? []);

    while (d.isSameOrBefore(e, 'day')) {
      res.push({
        start: d.startOf('day').toISOString(),
        end: d.endOf('day').toISOString(),
        resourceIds,
        title: d.format('D'),
        display: 'background',
        backgroundColor: 'transparent',
      });
      d = d.add(1, 'day');
    }

    /*setTasks((prev) => ({
      ...prev,
      backgroundEvents: res,
    }));*/
  };

  const handleEventDragStart = () => {
    isDraggingRef.current = true;
  };

  const handleEventDragStop = () => {
    isDraggingRef.current = false;
  };

  const handleResourceLabelClick = (resource) => {
    const calendarEl = calendarRef.current.getApi().el;

    const resourceRows = calendarEl.querySelectorAll(`.fc-timeline-lane[data-resource-id="${resource.id}"]`);

    const isAlreadySelected = resourceRows[0]?.classList.contains('selected-resource-row');

    calendarEl.querySelectorAll('.selected-resource-row').forEach((el) => {
      el.classList.remove('selected-resource-row');
    });

    if (!isAlreadySelected) {
      resourceRows.forEach((row) => {
        row.classList.add('selected-resource-row');
      });
    }
  };

  const deleteTask = () => {
    projectTaskResourcePlannerService.deleteTask(chipMenu.data.id).then(() => {
      setChipMenu(defChipMenu);
      setTaskChanged(true);
      setConfirmDialogData(defConfirmDialogData);
    });
  };

  const onDeleteClick = () => {
    setConfirmDialogData({
      open: true,
      message: 'Biztosan kitörli a foglalást?',
      handleChangeElement: () => deleteTask(),
      onHide: () => {
        setConfirmDialogData(defConfirmDialogData);
        setChipMenu(defChipMenu);
      },
    });
  };

  return (
    <div>
      <div ref={checkboxFiltersRef}>
        <GanttFilter
          initial={{ initialState, setInitialState }}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          options={options}
        />
      </div>
      <Tooltip
        open={Boolean(hoverDate)}
        followCursor
        arrow
        placement="top"
        title={<Typography fontWeight="bold">{hoverDate}</Typography>}
        slotProps={{
          tooltip: {
            sx: {
              color: '#514E6A',
              backgroundColor: '#fff',
              boxShadow: 'rgba(0,0,0,0.35) 0px 5px 15px',
              borderRadius: '30px',
            },
          },
          arrow: { sx: { color: '#fff' } },
        }}
      >
        <div
          className="taskListCalendar smallcells plannerDarkerBorder plannerSelectedCol"
          style={{ height: `calc(100dvh - ${headerOffset}px)` }}
        >
          <FullCalendar
            ref={calendarRef}
            height="100%"
            contentHeight="100%"
            customButtons={timeIntervalButtons}
            plugins={[resourceTimelinePlugin, interactionPlugin]}
            initialView="resourceTimelineYear"
            resources={tasks.resources}
            resourceOrder="visOrder"
            locales={[huLocale]}
            locale="hu"
            selectAllow={handleSelectPreview}
            selectable={true}
            selectMinDistance={5}
            slotLabelFormat={[
              { month: 'long', year: 'numeric', weekday: 'long' },
              { hour: 'numeric', minute: '2-digit' },
            ]}
            eventDragStart={handleEventDragStart}
            eventDragStop={handleEventDragStop}
            resourceLabelDidMount={(arg) => {
              const res = arg.resource;

              const frame = arg.el.querySelector('.fc-datagrid-cell-frame');
              const defaultColor = colorFinder(res?.extendedProps?.classification?.color)?.colorBgCode || '';

              frame.dataset.defaultColor = defaultColor;

              frame.style.backgroundColor = defaultColor;
              frame.style.cursor = 'pointer';

              frame.addEventListener('click', (e) => {
                e.stopPropagation();

                if (selectedResourceRef.current && selectedResourceRef.current !== frame) {
                  selectedResourceRef.current.classList.remove('selected-row');
                  selectedResourceRef.current.style.backgroundColor =
                    selectedResourceRef.current.dataset.defaultColor || '';
                }

                const isSelected = frame.classList.toggle('selected-row');

                frame.style.backgroundColor = isSelected ? '#03a1c04d' : frame.dataset.defaultColor;

                selectedResourceRef.current = isSelected ? frame : null;

                handleResourceLabelClick(res);
              });
            }}
            datesSet={handleDatesSet}
            events={[...tasks.taskEvents, ...tasks.backgroundEvents]}
            // disable drag/resize while long-press is active
            editable={Boolean(canEdit) && !longPressActive}
            eventStartEditable={Boolean(canEdit) && !longPressActive}
            eventDurationEditable={Boolean(canEdit) && !longPressActive}
            // hard-block any drop when long-press is active
            eventAllow={() => !longPressActive}
            /*
          dateClick={(info) => {
            if (!canEdit) return;

            // single-click when a task is being copied
            if (copyId) {
              if (dateClickTimerRef.current) clearTimeout(dateClickTimerRef.current);
              lastDateClickRef.current = { key: null, ts: 0 };
              setSelectedRowData({});
              handleDateClick(info);
              return;
            }

            const key = `${info.resource?.id}-${+info.date}`;
            const now = Date.now();
            const last = lastDateClickRef.current;
            const isDouble = last.key === key && now - last.ts < 300;

            if (isDouble) {
              if (dateClickTimerRef.current) clearTimeout(dateClickTimerRef.current);
              lastDateClickRef.current = { key: null, ts: 0 };
              setSelectedRowData({});
              handleDateClick(info);
              return;
            }

            lastDateClickRef.current = { key, ts: now };

            if (dateClickTimerRef.current) clearTimeout(dateClickTimerRef.current);
            dateClickTimerRef.current = setTimeout(() => {
              lastDateClickRef.current = { key: null, ts: 0 };
            }, 300);
          }}*/
            dateClick={(info) => {
              if (!canEdit) return;

              handleDateClick(info);
              setSelectedRowData({});
              setHoverDate(null);
            }}
            nowIndicator={true}
            eventResize={updateEvent}
            eventDrop={updateEvent}
            eventClick={(clickInfo) => {
              // handle only double-clicks, ignore background events
              if (clickInfo.event.display === 'background') return;
              if (!canEdit) return;
              const id = parseInt(clickInfo.event.id);

              switch (type) {
                case 'simpleTask':
                  return getTaskById(id, true);
                case 'planTask':
                  return getPlanTaskById(id, true);
                default:
                  return;
              }
            }}
            eventDidMount={(info) => {
              info.el.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                setChipMenu({
                  data: { id: info.event.id },
                  position: { mouseX: e.clientX + 2, mouseY: e.clientY - 6 },
                });
              });
            }}
            eventContent={(eventInfo) => {
              if (eventInfo.event.display !== 'background') {
                return (
                  <EventChip
                    defaultStyles={defaultStyles}
                    eventInfo={eventInfo}
                    onLongPressChange={setLongPressActive}
                    setCopyId={setCopyId}
                    isDraggingRef={isDraggingRef}
                  />
                );
              }
            }}
            headerToolbar={{
              left: `prev,next,today ${intervalButtons}`,
              center: 'title',
              right: 'resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth,resourceTimelineYear',
            }}
            views={calendarViews}
            slotDuration={timeInterval}
            resourceAreaWidth="15%"
            resourceAreaHeaderContent="Erőforrás"
            scrollTimeReset={false}
            resourceLabelClassNames={(data) => {
              if (!data.resource._resource.parentId) {
                return ['fc-res-group'];
              }
            }}
            nowIndicatorClassNames="fc-now-indicator"
          />

          <Box>
            {Object.keys(selectedRowData)?.length > 0 && (
              <Box textAlign="right" mr={5}>
                <Typography>Darabszám: {selectedRowData.daysCount}</Typography>
              </Box>
            )}
          </Box>

          {/* add: search dialog now includes its own input */}
          <SearchDialog
            open={searchDialogOpen && type === 'simpleTask'}
            searchQuery={searchQuery}
            handleClose={() => setSearchDialogOpen(false)}
            getTaskById={getTaskById}
            onSearchChange={setSearchQuery}
          />

          <PlanSearchDialog
            open={searchDialogOpen && type === 'planTask'}
            searchQuery={searchQuery}
            handleClose={() => setSearchDialogOpen(false)}
            getPlanTaskById={getPlanTaskById}
            onSearchChange={setSearchQuery}
          />

          <SimpleTaskDialog
            open={dialogOpen.open && type === 'simpleTask'}
            changed={{ taskChanged, setTaskChanged }}
            task={task}
            editing={dialogOpen.editing}
            submenuNames={submenuNames}
            dialogData={dialogData}
            handleClose={() => {
              setDialogOpen({ open: false, editing: false });
              setTask({});
            }}
          />

          <PlanTaskDialog
            open={dialogOpen.open && type === 'planTask'}
            changed={{ taskChanged, setTaskChanged }}
            task={task}
            editing={dialogOpen.editing}
            submenuNames={submenuNames}
            dialogData={dialogData}
            handleClose={() => {
              setDialogOpen({ open: false, editing: false });
              setTask({});
            }}
          />

          <EventChipMenu
            chipMenu={chipMenu}
            handleClose={() => setChipMenu(defChipMenu)}
            onDeleteClick={onDeleteClick}
          />

          <ConfirmDialogUI
            handleChangeElement={confirmDialogData.handleChangeElement}
            show={confirmDialogData.open}
            onHide={confirmDialogData.onHide}
            message={confirmDialogData.message}
          />
        </div>
      </Tooltip>
    </div>
  );
};

export default ProjectTaskResourcePlanner;
